<template>
  <div class="w-full p-4">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">User Groups</h2>
      <div class="text-xs text-gray-600">
        Click checkboxes to stage changes. Save commits everything; Discard reverts the page.
      </div>
    </div>

    <div class="flex flex-row gap-3 mb-3 items-center text-sm">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search user..."
        class="h-9 px-2 border border-gray-400 rounded-md w-64"
      />
      <span class="text-gray-500">{{ filteredUsers.length }} user<span v-if="filteredUsers.length !== 1">s</span></span>
      <button
        v-if="!showRecent"
        class="px-2 py-1 rounded border border-gray-400 text-xs text-gray-700 hover:bg-gray-100"
        title="Show the recent group changes pane"
        @click="showRecent = true"
      >Recent changes ›</button>
      <span v-if="pendingCount > 0" class="ml-auto inline-flex items-center gap-2">
        <span class="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">
          {{ pendingCount }} pending change<span v-if="pendingCount !== 1">s</span>
        </span>
        <button
          class="px-3 py-1 rounded bg-accent-primary text-white text-sm hover:bg-accent-secondary disabled:opacity-50"
          :disabled="saving"
          @click="saveAll"
        >{{ saving ? 'Saving...' : `Save ${pendingCount} change${pendingCount === 1 ? '' : 's'}` }}</button>
        <button
          class="px-3 py-1 rounded border border-gray-400 text-sm hover:bg-gray-100 disabled:opacity-50"
          :disabled="saving"
          @click="discardAll"
        >Discard</button>
      </span>
    </div>

    <div v-if="loading" class="text-center text-gray-500 py-6">Loading...</div>

    <div
      v-else
      class="grid gap-4"
      :class="showRecent ? 'lg:grid-cols-[3fr_1fr]' : ''"
    >
      <div class="border border-gray-400 rounded-md overflow-auto max-h-[calc(100vh-12rem)]">
        <table class="table-fixed text-sm">
          <thead class="text-white">
            <tr>
              <th class="sticky top-0 z-10 bg-dark-primary text-left px-3 py-2 align-bottom w-64 border-r border-gray-500">User</th>
              <th
                v-for="g in groups"
                :key="g.id"
                class="sticky top-0 z-10 bg-dark-primary px-3 align-bottom w-14 h-48 border-r border-gray-500"
              >
                <div
                  class="font-mono text-lg whitespace-nowrap mx-auto"
                  style="writing-mode: vertical-rl; transform: rotate(180deg);"
                  :title="g.label && g.label !== g.name ? `${g.label} (${g.name})` : g.name"
                >{{ g.label || g.name }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in filteredUsers" :key="u.id" class="border-t border-gray-200">
              <td class="px-3 py-2 whitespace-nowrap w-64 border-r border-gray-200">
                {{ fullName(u) }}
                <div class="text-xs text-gray-500">{{ u.username }}</div>
              </td>
              <td
                v-for="g in groups"
                :key="g.id"
                class="px-3 py-2 text-center w-14 border-r border-gray-200"
                :class="hasPending(u, g) ? 'bg-amber-50' : ''"
              >
                <input
                  type="checkbox"
                  class="h-7 w-7 cursor-pointer accent-accent-primary"
                  :checked="displayedState(u, g)"
                  :disabled="saving"
                  @change="togglePending(u, g)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <aside
        v-if="showRecent"
        class="border border-gray-400 rounded-md p-3 bg-white max-h-[calc(100vh-12rem)] overflow-auto"
      >
        <div class="flex items-center justify-between pb-2 mb-2 border-b border-gray-200">
          <h3 class="text-sm font-semibold">Recent changes</h3>
          <span class="flex items-center gap-2">
            <button
              class="text-xs text-gray-500 hover:underline"
              @click="loadRecentChanges"
            >refresh</button>
            <button
              class="text-xs text-gray-500 hover:underline"
              title="Hide this pane"
              @click="showRecent = false"
            >hide</button>
          </span>
        </div>
        <div v-if="loadingRecent" class="text-xs text-gray-500">Loading...</div>
        <p v-else-if="!recentChanges.length" class="text-xs text-gray-500">
          No group changes recorded yet.
        </p>
        <ul v-else class="space-y-2 text-xs">
          <li v-for="c in recentChanges" :key="c.id" class="border-b border-gray-100 pb-2 last:border-0 last:pb-0">
            <div>
              <span class="font-semibold">{{ shortName(c.actor) }}</span>
              <span class="text-gray-600">
                {{ c.action === 'add' ? 'added' : 'removed' }}
              </span>
              <span class="font-semibold">{{ shortName(c.user) }}</span>
              <span class="text-gray-600">
                {{ c.action === 'add' ? 'to' : 'from' }}
              </span>
              <code class="px-1 py-0.5 rounded bg-gray-100 text-gray-800">{{ c.group_name }}</code>
            </div>
            <div class="text-gray-400 mt-0.5">{{ relativeTime(c.created_at) }}</div>
          </li>
        </ul>
      </aside>
    </div>
  </div>
</template>

<script>
export default { name: "user-groups-admin" };
</script>

<script setup>
import { ref, computed, reactive, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import {
  getUserGroupAdminUsers, getManagedGroups, addUserToGroup, removeUserFromGroup,
  getUserGroupRecentChanges,
} from "@/services/ExpenseAdmin.js";

const store = useStore();
const $toast = useToast();

const loading = ref(true);
const loadingRecent = ref(false);
const saving = ref(false);
const users = ref([]);
const groups = ref([]);
const recentChanges = ref([]);

// Collapsed by default: the table is the job, and the full width is worth
// more than a log most visits never read. Remembered per browser so somebody
// who does want it open does not reopen it every visit. Wrapped because
// storage throws in a private window, and a lost preference must not take
// the page with it.
const RECENT_PANE_KEY = 'userGroupsAdmin.showRecent';
const showRecent = ref(readShowRecent());

function readShowRecent() {
  try {
    return window.localStorage.getItem(RECENT_PANE_KEY) === 'true';
  } catch (e) {
    return false;
  }
}

watch(showRecent, value => {
  try {
    window.localStorage.setItem(RECENT_PANE_KEY, value ? 'true' : 'false');
  } catch (e) {
    // A browser that will not remember it is not a reason to fail the page.
  }
});
const searchQuery = ref("");

// Pending desired states keyed by `${userId}:${groupId}` → boolean.
// A key is present in `pending` only when the desired state differs
// from the saved state — toggling a checkbox back to its saved state
// removes the entry instead of recording a no-op.
const pending = reactive({});

function fullName(u) {
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return name || u.username;
}

function shortName(u) {
  if (!u) return "—";
  const first = (u.first_name || "").trim();
  const last = (u.last_name || "").trim();
  if (first || last) return `${first} ${last}`.trim();
  return u.username || "";
}

function userInGroup(u, groupId) {
  return (u.groups || []).some((g) => g.id === groupId);
}

function pendingKey(u, g) {
  return `${u.id}:${g.id}`;
}

function hasPending(u, g) {
  return Object.prototype.hasOwnProperty.call(pending, pendingKey(u, g));
}

function displayedState(u, g) {
  const k = pendingKey(u, g);
  if (k in pending) return pending[k];
  return userInGroup(u, g.id);
}

function togglePending(u, g) {
  const k = pendingKey(u, g);
  const saved = userInGroup(u, g.id);
  const desired = !displayedState(u, g);
  if (desired === saved) {
    delete pending[k];
  } else {
    pending[k] = desired;
  }
}

const pendingCount = computed(() => Object.keys(pending).length);

const filteredUsers = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return users.value;
  return users.value.filter((u) =>
    fullName(u).toLowerCase().includes(q) ||
    (u.username || "").toLowerCase().includes(q) ||
    (u.email || "").toLowerCase().includes(q)
  );
});

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

async function loadMatrix() {
  loading.value = true;
  const token = store.state.auth.token;
  try {
    const [u, g] = await Promise.all([getUserGroupAdminUsers(token), getManagedGroups(token)]);
    users.value = u || [];
    groups.value = g || [];
  } catch (error) {
    $toast.open({ message: "Failed to load user groups", type: "error", duration: 3000 });
  }
  loading.value = false;
}

async function loadRecentChanges() {
  loadingRecent.value = true;
  const token = store.state.auth.token;
  try {
    recentChanges.value = await getUserGroupRecentChanges(token, 15) || [];
  } catch (error) {
    recentChanges.value = [];
  }
  loadingRecent.value = false;
}

function discardAll() {
  for (const k of Object.keys(pending)) delete pending[k];
}

async function saveAll() {
  if (pendingCount.value === 0) return;
  saving.value = true;
  const token = store.state.auth.token;
  const entries = Object.entries(pending);
  const userById = new Map(users.value.map((u) => [u.id, u]));
  const groupById = new Map(groups.value.map((g) => [g.id, g]));
  let okCount = 0;
  let failCount = 0;
  // Apply sequentially so per-row errors are clear; volume is small (a
  // handful of toggles per save).
  for (const [k, desired] of entries) {
    const [userId, groupId] = k.split(":").map((s) => parseInt(s, 10));
    try {
      if (desired) {
        await addUserToGroup(token, userId, groupId);
      } else {
        await removeUserFromGroup(token, userId, groupId);
      }
      // Mutate the local user.groups array so the displayed state is
      // immediately consistent without a full refetch.
      const u = userById.get(userId);
      const g = groupById.get(groupId);
      if (u && g) {
        if (desired) {
          if (!u.groups.some((x) => x.id === groupId)) {
            u.groups = [...u.groups, { id: groupId, name: g.name }];
          }
        } else {
          u.groups = u.groups.filter((x) => x.id !== groupId);
        }
      }
      delete pending[k];
      okCount += 1;
    } catch (error) {
      failCount += 1;
    }
  }
  saving.value = false;
  if (okCount > 0) {
    $toast.open({
      message: `Saved ${okCount} change${okCount === 1 ? '' : 's'}${failCount ? ` — ${failCount} failed` : ''}`,
      type: failCount ? 'warning' : 'success',
      duration: 2500,
    });
    if (showRecent.value) await loadRecentChanges();
  } else if (failCount > 0) {
    $toast.open({ message: 'All changes failed to save', type: 'error', duration: 3000 });
  }
}

// Fetched when the pane is first opened rather than on mount: collapsed is
// now the default, so most visits would be paying for a list nobody sees.
// After a save the pane reloads itself, but only if it is open to show it.
watch(showRecent, async value => {
  if (value && !recentChanges.value.length && !loadingRecent.value) {
    await loadRecentChanges();
  }
});

onMounted(async () => {
  await loadMatrix();
  if (showRecent.value) await loadRecentChanges();
});
</script>
